import { useState, useCallback, useEffect, useRef } from 'react';
import { generateUUID } from '@/shared/utils/uuid';
import {
  addFacility,
  patchFacility,
  createTempFacility,
  finalizeFacility,
  getDraftFacility,
} from '../api/addFacilityApiService';
import {
  getFacilitySession,
  setFacilitySession,
  clearFacilitySession,
  getDraftFacilityId,
  setDraftFacilityId as saveDraftFacilityId,
  clearDraftFacilityId,
} from '../utils/facilitySessionStorage';
import { createDraftEmployee } from '../api/addEmployeeApiService';
import { listFacilityUsers } from '../api/listFacilityUserApiService';
import { EQUIPMENT_ERROR_MESSAGES, EMPLOYEE_ERROR_MESSAGES } from '../errors';
import {
  formatNip,
  validateEmployeeName,
  validateEmail,
  validateEmployeePhone,
} from '../utils/validation';
import { FACILITY_LABELS } from '../constants/facilityLabels';
import type {
  FacilityEntry,
  FacilityEntryErrors,
} from '../types/addFacilityType';
import type {
  EmployeeEntry,
  EmployeeEntryErrors,
} from '../types/addEmployeeType';

// ── Factories ────────────────────────────────────────────────────────────────

const createEmptyEntry = (): FacilityEntry => ({
  id: generateUUID(),
  name: '',
  nip: '',
  phone: '',
  countryCode: '+48',
  address1: '',
  address2: '',
  city: '',
  state: '',
  zip: '',
  country: 'Polska',
});

const createEmptyErrors = (): FacilityEntryErrors => ({
  name: '',
  nip: '',
  phone: '',
  address1: '',
  city: '',
  state: '',
  zip: '',
});

const createEmptyEmployeeEntry = (): EmployeeEntry => ({
  id: generateUUID(),
  name: '',
  email: '',
  phone: '',
  countryCode: '+48',
});

// ── Validators ───────────────────────────────────────────────────────────────
// TODO: move facility validators to utils/validation.ts (same pattern as clientManagement)

const validateNip = (nip: string): string => {
  const digits = nip.replace(/\D/g, '');
  if (!digits) {
    return EQUIPMENT_ERROR_MESSAGES.REQUIRED;
  }
  if (digits.length !== 10) {
    return 'Nieprawidłowy NIP';
  }
  return '';
};

const validatePhone = (phone: string): string => {
  const digits = phone.replace(/\D/g, '');
  if (!digits) {
    return EQUIPMENT_ERROR_MESSAGES.REQUIRED;
  }
  if (digits.length !== 9) {
    return 'Niepoprawny numer telefonu';
  }
  return '';
};

const validateZip = (zip: string): string => {
  if (!zip.trim()) {
    return '';
  }
  if (!/^\d{2}-\d{3}$/.test(zip.trim())) {
    return 'Nieprawidłowy format kodu pocztowego.';
  }
  return '';
};

const validateAll = (entry: FacilityEntry): FacilityEntryErrors => ({
  name: entry.name.trim() ? '' : EQUIPMENT_ERROR_MESSAGES.REQUIRED,
  nip: validateNip(entry.nip),
  phone: validatePhone(entry.phone),
  address1: entry.address1.trim() ? '' : EQUIPMENT_ERROR_MESSAGES.REQUIRED,
  city: entry.city.trim() ? '' : EQUIPMENT_ERROR_MESSAGES.REQUIRED,
  state: entry.state.trim() ? '' : EQUIPMENT_ERROR_MESSAGES.REQUIRED,
  zip: validateZip(entry.zip),
});

const hasErrors = (errors: FacilityEntryErrors) =>
  Object.values(errors).some(Boolean);

const validateEmployee = (entry: EmployeeEntry): EmployeeEntryErrors => ({
  name: validateEmployeeName(entry.name),
  email: validateEmail(entry.email),
  phone: validateEmployeePhone(entry.phone),
  gender: '',
  academicTitle: '',
});

const hasEmployeeErrors = (errors: EmployeeEntryErrors) =>
  Object.values(errors).some(Boolean);

// ── Hook ─────────────────────────────────────────────────────────────────────

export const useAddFacilityPage = (clientId: string | null) => {
  const [entry, setEntry] = useState<FacilityEntry>(createEmptyEntry);
  const [errors, setErrors] = useState<FacilityEntryErrors>(createEmptyErrors);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isCancelModalOpen, setIsCancelModalOpen] = useState(false);
  const [isSnackbarOpen, setIsSnackbarOpen] = useState(false);
  const [snackbarError, setSnackbarError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [isSuccess, setIsSuccess] = useState(false);
  const [serverFacilityId, setServerFacilityId] = useState<string | null>(null);
  const [draftFacilityId, setDraftFacilityId] = useState<string | null>(null);
  const [conflictError, setConflictError] = useState<
    { employeeId: string; userId: string; email: string }[] | null
  >(null);
  const [serialNumberConflict, setSerialNumberConflict] = useState<
    string | null
  >(null);
  const [isRehydrating, setIsRehydrating] = useState(true);
  const [initialEmployeeCount, setInitialEmployeeCount] = useState(0);

  // Restore temp and draft facility IDs from sessionStorage once clientId is available
  const restoredRef = useRef(false);
  useEffect(() => {
    if (!clientId || restoredRef.current) {
      return;
    }
    restoredRef.current = true;
    const stored = getFacilitySession(clientId);
    const restoredFacilityId = stored[0] ?? null;
    if (restoredFacilityId) {
      setServerFacilityId(restoredFacilityId);
    }
    const storedDraft = getDraftFacilityId(clientId);
    if (storedDraft || restoredFacilityId) {
      const fetches: Promise<unknown>[] = [];
      if (storedDraft) {
        setDraftFacilityId(storedDraft);
        fetches.push(
          getDraftFacility(storedDraft).then((result) => {
            if (result.success && result.fields) {
              const f = result.fields;
              setEntry((prev) => ({
                ...prev,
                name: f.name ?? prev.name,
                nip: f.nip ?? prev.nip,
                phone: f.phoneNumber ?? prev.phone,
                countryCode: f.countryCode ?? prev.countryCode,
                address1: f.addressLine1 ?? prev.address1,
                address2: f.addressLine2 ?? prev.address2,
                city: f.city ?? prev.city,
                state: f.state ?? prev.state,
                zip: f.zipCode ?? prev.zip,
                country: f.country ?? prev.country,
              }));
            }
          })
        );
      }
      if (restoredFacilityId) {
        fetches.push(
          listFacilityUsers({ facilityId: restoredFacilityId, limit: 1 }).then(
            (result) => {
              if (result.success && result.data) {
                setInitialEmployeeCount(result.data.pagination.totalCount);
              }
            }
          )
        );
      }
      void Promise.all(fetches).then(() => setIsRehydrating(false));
    } else {
      setIsRehydrating(false);
    }
  }, [clientId]);

  // Employee state
  const [employeeList, setEmployeeList] = useState<EmployeeEntry[]>(() => [
    createEmptyEmployeeEntry(),
  ]);
  const [employeeErrorsMap, setEmployeeErrorsMap] = useState<
    Record<string, EmployeeEntryErrors>
  >({});
  const [isEmployeeSubmitting, setIsEmployeeSubmitting] = useState(false);
  const [isEmployeeSuccessOpen, setIsEmployeeSuccessOpen] = useState(false);

  // ── Derived state ─────────────────────────────────────────────────────────

  // employeeList is always non-empty (initialized with one entry, never emptied)
  const activeEmployeeEntry =
    employeeList.find((e) => !e.serverEmployeeId) ??
    employeeList[employeeList.length - 1] ??
    createEmptyEmployeeEntry();

  const isEmployeeFormDirty =
    !activeEmployeeEntry.serverEmployeeId &&
    (activeEmployeeEntry.name !== '' ||
      activeEmployeeEntry.email !== '' ||
      activeEmployeeEntry.phone !== '' ||
      activeEmployeeEntry.gender !== undefined ||
      !!(activeEmployeeEntry.academicTitle ?? ''));

  const submittedEmployeeCount = employeeList.filter(
    (e) => !!e.serverEmployeeId
  ).length;

  const isDirty =
    entry.name !== '' ||
    entry.nip !== '' ||
    entry.phone !== '' ||
    entry.address1 !== '' ||
    entry.address2 !== '' ||
    entry.city !== '' ||
    entry.state !== '' ||
    entry.zip !== '' ||
    isEmployeeFormDirty ||
    submittedEmployeeCount > 0;

  // ── Facility field handlers ───────────────────────────────────────────────

  const updateField = useCallback(
    (
      field: keyof Pick<
        FacilityEntry,
        'name' | 'address1' | 'address2' | 'city' | 'state' | 'zip'
      >,
      value: string
    ) => {
      setEntry((prev) => ({ ...prev, [field]: value }));
      setErrors((prev) => ({ ...prev, [field]: '' }));
    },
    []
  );

  const handleNipChange = useCallback((raw: string) => {
    setEntry((prev) => ({ ...prev, nip: formatNip(raw) }));
    setErrors((prev) => ({ ...prev, nip: '' }));
  }, []);

  const handlePhoneChange = useCallback((raw: string) => {
    const digits = raw.replace(/\D/g, '').slice(0, 9);
    setEntry((prev) => ({ ...prev, phone: digits }));
    setErrors((prev) => ({ ...prev, phone: '' }));
  }, []);

  // ── Employee field handlers ───────────────────────────────────────────────

  const updateEmployeeField = useCallback(
    (
      id: string,
      field: keyof Pick<
        EmployeeEntry,
        'name' | 'email' | 'phone' | 'academicTitle'
      >,
      value: string
    ) => {
      setEmployeeList((prev) =>
        prev.map((e) => (e.id === id ? { ...e, [field]: value } : e))
      );
      setEmployeeErrorsMap((prev) => {
        if (!prev[id]) {
          return prev;
        }
        return { ...prev, [id]: { ...prev[id], [field]: '' } };
      });
    },
    []
  );

  const updateEmployeeGender = useCallback(
    (id: string, value: 'male' | 'female' | undefined) => {
      setEmployeeList((prev) =>
        prev.map((e) => {
          if (e.id !== id) {
            return e;
          }

          const { gender: _g, ...rest } = e;
          return value !== undefined
            ? ({ ...rest, gender: value } satisfies EmployeeEntry)
            : (rest as EmployeeEntry);
        })
      );
    },
    []
  );

  // ── Temp facility ─────────────────────────────────────────────────────────

  const getOrCreateTempFacility = useCallback(
    async (clientId: string): Promise<string | null> => {
      if (serverFacilityId) {
        return serverFacilityId;
      }

      const result = await createTempFacility(clientId);
      if (!result.success || !result.facilityId) {
        setSnackbarError(
          result.error ?? EQUIPMENT_ERROR_MESSAGES.DEFAULT_ERROR
        );
        setIsSnackbarOpen(true);
        return null;
      }

      setServerFacilityId(result.facilityId);
      setFacilitySession(clientId, [result.facilityId]);
      return result.facilityId;
    },
    [serverFacilityId]
  );

  // ── Submit employee ───────────────────────────────────────────────────────

  const submitEmployee = useCallback(
    async (clientId: string) => {
      const active = employeeList.find((e) => !e.serverEmployeeId);
      if (!active) {
        return;
      }

      const errs = validateEmployee(active);
      setEmployeeErrorsMap((prev) => ({ ...prev, [active.id]: errs }));
      if (hasEmployeeErrors(errs)) {
        return;
      }

      // Check for duplicate email within the current session before hitting the API.
      // The backend Cognito check won't catch same-session drafts (they have no Cognito
      // account yet); the DB partial unique index only fires at finalization.
      const normalizedEmail = active.email.trim().toLowerCase();
      const isDuplicateEmail = employeeList.some(
        (e) =>
          e.serverEmployeeId && e.email.trim().toLowerCase() === normalizedEmail
      );
      const emptyErrs = {
        name: '',
        email: '',
        phone: '',
        gender: '',
        academicTitle: '',
      };

      if (isDuplicateEmail) {
        setEmployeeErrorsMap((prev) => ({
          ...prev,
          [active.id]: {
            ...(prev[active.id] ?? emptyErrs),
            email: EMPLOYEE_ERROR_MESSAGES.EMAIL_ALREADY_EXISTS,
          },
        }));
        return;
      }

      setIsEmployeeSubmitting(true);
      try {
        const facilityId = await getOrCreateTempFacility(clientId);
        if (!facilityId) {
          // Error already shown by getOrCreateTempFacility
          return;
        }

        const employeePayload = {
          name: active.name.trim(),
          email: active.email.trim(),
          phoneNumber: active.phone.trim(),
          countryCode: active.countryCode,
          ...(active.gender !== undefined && { gender: active.gender }),
          ...(active.academicTitle?.trim()
            ? { academicTitle: active.academicTitle.trim() }
            : {}),
        };
        const result = await createDraftEmployee(facilityId, employeePayload);

        if (!result.success) {
          if (result.error === EMPLOYEE_ERROR_MESSAGES.EMAIL_ALREADY_EXISTS) {
            setEmployeeErrorsMap((prev) => ({
              ...prev,
              [active.id]: {
                ...(prev[active.id] ?? emptyErrs),
                email:
                  result.error ?? EMPLOYEE_ERROR_MESSAGES.EMAIL_ALREADY_EXISTS,
              },
            }));
          } else {
            setSnackbarError(
              result.error ?? EMPLOYEE_ERROR_MESSAGES.DEFAULT_ERROR
            );
            setIsSnackbarOpen(true);
          }
          return;
        }

        const employeeId = result.employeeId;
        if (!employeeId) {
          return;
        }

        setEmployeeList((prev) => [
          ...prev.map((e) =>
            e.id === active.id ? { ...e, serverEmployeeId: employeeId } : e
          ),
          createEmptyEmployeeEntry(),
        ]);
        setIsEmployeeSuccessOpen(true);
      } catch {
        setSnackbarError(EQUIPMENT_ERROR_MESSAGES.UNEXPECTED_ERROR);
        setIsSnackbarOpen(true);
      } finally {
        setIsEmployeeSubmitting(false);
      }
    },
    [employeeList, getOrCreateTempFacility]
  );

  // ── Submit facility ───────────────────────────────────────────────────────

  const submit = useCallback(
    async (clientId: string) => {
      const validationErrors = validateAll(entry);
      setErrors(validationErrors);
      if (hasErrors(validationErrors)) {
        return;
      }

      setIsSubmitting(true);
      try {
        // Phase 1: ensure a fully-populated facility exists on the server
        let currentDraftFacilityId = draftFacilityId;

        const facilityIdOnServer = draftFacilityId ?? serverFacilityId;

        if (facilityIdOnServer) {
          // Facility already exists on server (is_valid=false draft) — PATCH it with
          // current form data. This covers both the "temp facility via employee/equipment"
          // path and the "retry after failed finalize" path, ensuring the server always
          // reflects the latest form state before we finalize.
          const patchResult = await patchFacility(
            facilityIdOnServer,
            {
              name: entry.name,
              nip: entry.nip,
              phoneNumber: entry.phone,
              countryCode: entry.countryCode,
              addressLine1: entry.address1,
              city: entry.city,
              state: entry.state,
              country: entry.country,
              addressLine2: entry.address2 || undefined,
              zipCode: entry.zip || undefined,
            },
            true
          );

          if (!patchResult.success) {
            setSnackbarError(
              patchResult.error ?? EQUIPMENT_ERROR_MESSAGES.DEFAULT_ERROR
            );
            setIsSnackbarOpen(true);
            return;
          }

          currentDraftFacilityId = facilityIdOnServer;
          if (!draftFacilityId) {
            setDraftFacilityId(currentDraftFacilityId);
            saveDraftFacilityId(clientId, currentDraftFacilityId);
          }
        } else {
          // No facility on server yet — POST to create it
          const result = await addFacility(
            clientId,
            entry.name,
            entry.nip,
            entry.phone,
            entry.countryCode,
            entry.address1,
            entry.city,
            entry.state,
            entry.country,
            entry.address2 || undefined,
            entry.zip || undefined
          );

          if (!result.success || !result.facilityId) {
            setSnackbarError(
              result.error ?? EQUIPMENT_ERROR_MESSAGES.DEFAULT_ERROR
            );
            setIsSnackbarOpen(true);
            return;
          }

          currentDraftFacilityId = result.facilityId;
          setDraftFacilityId(currentDraftFacilityId);
          saveDraftFacilityId(clientId, currentDraftFacilityId);
        }

        // Phase 2: finalize (activates facility + employees + equipment atomically)
        const finalizeResult = await finalizeFacility(
          clientId,
          currentDraftFacilityId
        );

        if (finalizeResult.success) {
          clearFacilitySession(clientId);
          clearDraftFacilityId(clientId);
          setSuccessMessage(FACILITY_LABELS.ADD_FACILITY_SUCCESS);
          setIsSuccess(true);
        } else if (finalizeResult.serialNumberConflict) {
          setSerialNumberConflict(finalizeResult.serialNumberConflict);
        } else if (finalizeResult.conflictDetails) {
          setConflictError(finalizeResult.conflictDetails);
        } else {
          setSnackbarError(
            finalizeResult.error ?? EQUIPMENT_ERROR_MESSAGES.DEFAULT_ERROR
          );
          setIsSnackbarOpen(true);
          if (finalizeResult.isNetworkError) {
            // Network failure — user's connection is gone; they'll likely refresh or
            // navigate. Clear session so next visit starts with a clean slate.
            clearFacilitySession(clientId);
            clearDraftFacilityId(clientId);
            setServerFacilityId(null);
            setDraftFacilityId(null);
            setEmployeeList([createEmptyEmployeeEntry()]);
          }
        }
      } catch {
        setSnackbarError(EQUIPMENT_ERROR_MESSAGES.UNEXPECTED_ERROR);
        setIsSnackbarOpen(true);
      } finally {
        setIsSubmitting(false);
      }
    },
    [entry, serverFacilityId, draftFacilityId]
  );

  // ── Cancel helpers ────────────────────────────────────────────────────────

  const clearSession = useCallback(() => {
    if (clientId) {
      clearFacilitySession(clientId);
      clearDraftFacilityId(clientId);
    }
    setServerFacilityId(null);
    setDraftFacilityId(null);
  }, [clientId]);

  const handleCancel = useCallback(() => {
    setIsCancelModalOpen(true);
  }, []);

  const handleCloseCancelModal = useCallback(
    () => setIsCancelModalOpen(false),
    []
  );

  const handleCloseSnackbar = useCallback(() => setIsSnackbarOpen(false), []);

  return {
    // Facility
    entry,
    errors,
    isSubmitting,
    isDirty,
    isCancelModalOpen,
    isSnackbarOpen,
    snackbarError,
    successMessage,
    isSuccess,
    serverFacilityId,
    draftFacilityId,
    conflictError,
    serialNumberConflict,
    clearSerialNumberConflict: useCallback(
      () => setSerialNumberConflict(null),
      []
    ),
    isRehydrating,
    initialEmployeeCount,
    getOrCreateTempFacility,
    updateField,
    handleNipChange,
    handlePhoneChange,
    submit,
    clearSession,
    handleCancel,
    handleCloseCancelModal,
    handleCloseSnackbar,
    // Employee
    employeeList,
    activeEmployeeEntry,
    employeeErrorsMap,
    isEmployeeFormDirty,
    isEmployeeSubmitting,
    isEmployeeSuccessOpen,
    submittedEmployeeCount,
    setIsEmployeeSuccessOpen,
    updateEmployeeField,
    updateEmployeeGender,
    submitEmployee,
  };
};
